"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import {
  HEARTBEAT_ACTIVE_INTERVAL_MS as ACTIVE_INTERVAL_MS,
  HEARTBEAT_IDLE_INTERVAL_MS as IDLE_INTERVAL_MS,
} from "@/lib/heartbeat-timing";
import { UnderhallVarning } from "@/components/underhall/UnderhallVarning";

// Poll interval is driven by the server response:
//   - aktivLektion=true  → 20 s
//   - aktivLektion=false → 30 s (less load outside a lesson)
//
// These live in lib/heartbeat-timing.ts together with the server-side write
// throttle and the activity window derived from both. Changing one here without
// the others is what produced the 2026-08-11 bug where working students showed
// as inactive — so change them there, not here.

/**
 * Skickar POST /api/heartbeat när fliken är synlig. Server uppdaterar
 * User.lastSeenWeb + loggar minute-bucket i LektionAktivitet om eleven har
 * en aktiv lektion. Servern returnerar { aktivLektion: bool } så klienten
 * vet om den ska polla tätt (under lektion) eller glest.
 *
 * The response can also carry maintenance fields: `varningSekunder` (less
 * than VARNING_FONSTER_SEK left until the lock) or `underhall: true` (the
 * lock is already on). Bypass accounts (admin/@testskola) are already
 * filtered out server-side — they never get either field, so the client
 * just acts on whatever it receives without checking role itself. See
 * `UnderhallVarning` for the pre-warning popup.
 *
 * Mounted globally in app/layout.tsx, but not run on the phone's /m paths (see
 * heartbeatPaSokvag). /api/heartbeat sits behind the proxy's login gate:
 * without a session cookie the proxy answers 307 → /login before the route is
 * reached (the route's own 401 only for a cookie the proxy lets through), and
 * fetch follows the redirect — that answer is no heartbeat and is ignored.
 */
export default function HeartbeatBeacon() {
  return <HeartbeatBeaconForSokvag pathname={usePathname()} />;
}

/**
 * The paired phone's pages (/m, /m/scan, /m/expired, /m/klass-join/…) get no web
 * heartbeat (F-2026-007, stresstest fingerprint B14 F5): the phone holds only a
 * mobile session, so every beat was a 307 → POST /login — two wasted requests
 * per paired phone every 30 s. MobileShell sends the phone's own heartbeat
 * (/api/mobile-pair/heartbeat). Everywhere else — including /mina-prov — the
 * beacon runs as before; an unknown path (null) too.
 *
 * UV (accepted): a phone that ALSO holds a web cookie (the student is logged in on
 * the web in the same browser) used to send a real web heartbeat from /m. On /m it
 * now does not, so there it no longer updates lastSeenWeb or web activity and gets
 * no maintenance pre-warning. Off /m the beacon runs as before.
 */
export function heartbeatPaSokvag(pathname: string | null): boolean {
  if (pathname === null) return true;
  return !(pathname === "/m" || pathname.startsWith("/m/"));
}

/** Split from the default export so the path decision is testable without a DOM (HeartbeatBeacon.test.ts). */
export function HeartbeatBeaconForSokvag({ pathname }: { pathname: string | null }) {
  return heartbeatPaSokvag(pathname) ? <WebbHeartbeat /> : null;
}

/** The heartbeat loop itself; unmounting (navigating onto /m) stops it. */
export function WebbHeartbeat() {
  const [varningSekunder, setVarningSekunder] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    let hasActiveLesson = false;

    function currentInterval() {
      return hasActiveLesson ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS;
    }

    async function ping() {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      try {
        const res = await fetch("/api/heartbeat", {
          method: "POST",
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = (await res.json()) as {
          aktivLektion?: boolean;
          varningSekunder?: number;
          underhall?: boolean;
          avslutaSeb?: boolean;
        };
        if (cancelled) return;
        // The student sits in Safe Exam Browser but the lock has ended (lesson/prov over, or the teacher unticked it):
        // opening SEB's quit URL makes SEB close itself. Outside SEB the server never sends this.
        if (data.avslutaSeb) {
          window.location.assign("/lektion/klar");
          return;
        }
        hasActiveLesson = !!data.aktivLektion;
        // The lock being on already makes a countdown meaningless — and once
        // it engages the 503 page takes over anyway, so drop the field here
        // rather than teach the popup about `underhall` too.
        setVarningSekunder(
          data.underhall || typeof data.varningSekunder !== "number" ? null : data.varningSekunder,
        );
      } catch {
        // Tyst — försök igen nästa intervall
      }
    }

    function loop() {
      if (cancelled) return;
      timer = window.setTimeout(async () => {
        await ping();
        loop();
      }, currentInterval());
    }

    // Initial ping + starta loopen
    ping().then(() => {
      if (!cancelled) loop();
    });

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        // Ping direkt + restart loopen så intervallet matchar nuvarande state
        if (timer !== null) window.clearTimeout(timer);
        ping().then(() => {
          if (!cancelled) loop();
        });
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return <UnderhallVarning varningSekunder={varningSekunder} />;
}
